/**
 * `/support/:ticketId` — the thread.
 *
 * A conversation, not a table. The two decisions worth naming:
 *
 * **Whose turn it is, said out loud.** The header states it in a sentence
 * rather than showing a status chip and leaving the customer to work it out.
 * "With us" versus "your move" is the only thing most people open this page to
 * find out, and `needsReply` is the shared derivation the console uses for the
 * same judgement — so the two surfaces can never disagree about who is waiting.
 *
 * **The reply box is always there, on every status.** Replying to a resolved
 * ticket reopens it, which is exactly what the resolution email promises in as
 * many words. Hiding the box on a closed ticket would make a liar of that
 * email and push somebody into opening a duplicate.
 */

import { AttachmentPicker } from "@/components/AttachmentPicker";
import { PageHeader } from "@/components/PageHeader";
import { Notice, Strip, StripBody, StripHead, Tag } from "@/components/deck";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { PortalError, portalApi, useApiMutation, useApiQuery } from "@/lib/api";
import type { AttachmentMeta, TicketDetail, TicketMessage } from "@/lib/api";
import type { PickedAttachment } from "@/lib/attachments";
import { releasePreviews, toOutgoing } from "@/lib/attachments";
import { relative, stamp } from "@/lib/format";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ArrowLeft, CheckCheck, Mail } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useParams } from "react-router";
import { MAX_MESSAGE_LEN, needsReply } from "../../shared/support";

export function TicketDetailRoute() {
  const { ticketId = "" } = useParams<{ ticketId: string }>();
  const ticket = useApiQuery([ticketId], (signal) => portalApi.getTicket(ticketId, signal), {
    suppressToast: true,
  });

  if (ticket.isLoading) {
    return (
      <>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-4 h-8 w-2/3" />
        <Strip className="mt-7">
          <StripBody className="flex flex-col gap-3 pl-5">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </StripBody>
        </Strip>
      </>
    );
  }

  if (ticket.error) {
    const missing = ticket.error instanceof PortalError && ticket.error.status === 404;
    return (
      <>
        <PageHeader
          code="SUP"
          label="Ticket"
          title={missing ? "We cannot find that ticket" : "Could not load this ticket"}
          actions={<BackButton />}
        />
        <Notice
          tone={missing ? "marker" : "hold"}
          label={missing ? "Not on this account" : "Load failed"}
          action={
            missing ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/support">All tickets</Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={ticket.refetch}>
                Try again
              </Button>
            )
          }
        >
          {missing
            ? "This ticket is not on the account you are signed in with."
            : ticket.error.message}
        </Notice>
      </>
    );
  }

  if (!ticket.data) return null;
  return <Thread ticket={ticket.data} onChanged={ticket.refetch} />;
}

function Thread({ ticket, onChanged }: { ticket: TicketDetail; onChanged: () => void }) {
  const { pushToast } = useToast();
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<PickedAttachment[]>([]);

  const reply = useApiMutation(
    () => portalApi.replyToTicket(ticket.id, message.trim(), toOutgoing(attachments)),
    {
      suppressToast: true,
      onSuccess: (result) => {
        setMessage("");
        releasePreviews(attachments);
        setAttachments([]);
        if (result.reopened) {
          pushToast({ kind: "success", title: "Sent — and the ticket is open again." });
        }
        onChanged();
      },
    },
  );

  const resolve = useApiMutation(() => portalApi.resolveTicket(ticket.id), {
    onSuccess: () => {
      pushToast({ kind: "success", title: "Marked as sorted. Reply any time to reopen it." });
      onChanged();
    },
  });

  const waiting = needsReply(ticket);
  const open = ticket.status === "open";
  const canSend = message.trim().length > 0 || attachments.length > 0;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSend) return;
    reply.mutate(undefined);
  }

  return (
    <>
      <PageHeader
        code="SUP"
        label={`Ticket #${ticket.number}`}
        title={ticket.subject}
        adornment={
          open ? (
            <Tag tone={waiting ? "marker" : "cleared"} dot={false}>
              {waiting ? "WITH US" : "YOUR MOVE"}
            </Tag>
          ) : (
            <Tag dot={false}>{ticket.status.toUpperCase()}</Tag>
          )
        }
        description={
          open
            ? waiting
              ? "We have this. You will get an email the moment there is an answer."
              : "We answered — read below. Add anything else and it comes straight back to us."
            : "This one is closed. Replying below opens it again; you do not need to start over."
        }
        actions={<BackButton />}
      />

      <ol className="flex list-none flex-col gap-4 p-0">
        {ticket.messages.map((m) => (
          <li key={m.id}>
            <Message message={m} ticketId={ticket.id} />
          </li>
        ))}
      </ol>

      <section aria-labelledby="reply-head" className="mt-6">
        <Strip>
          <StripHead
            title={open ? "Reply" : "Reply and reopen"}
            as="h2"
            id="reply-head"
            note={
              open
                ? undefined
                : "Anything you add here brings this ticket back to us as an open case."
            }
          >
            {open && !waiting ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => resolve.mutate(undefined)}
                disabled={resolve.isPending}
              >
                <CheckCheck aria-hidden="true" />
                {resolve.isPending ? "Closing…" : "This is sorted"}
              </Button>
            ) : null}
          </StripHead>

          {/* The panel is page-width; the box you write in is not. See the
              width note in support.tsx — a reply is prose, and prose typed
              across 64rem is worse to write and worse to re-read. */}
          <StripBody className="max-w-[46rem] pl-5">
            <form onSubmit={onSubmit} noValidate>
              <label htmlFor="reply-body" className="sr-only">
                Your reply
              </label>
              <Textarea
                id="reply-body"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add anything that might help…"
                rows={5}
                maxLength={MAX_MESSAGE_LEN}
                disabled={reply.isPending}
              />

              <div className="mt-4">
                <AttachmentPicker
                  attachments={attachments}
                  onChange={setAttachments}
                  disabled={reply.isPending}
                />
              </div>

              {reply.error ? (
                <Notice tone="hold" label="Could not send" className="mt-4">
                  {reply.error.message}
                </Notice>
              ) : null}

              <div className="mt-5 flex flex-wrap items-center gap-4">
                <Button type="submit" disabled={reply.isPending || !canSend}>
                  {reply.isPending ? "Sending…" : "Send"}
                </Button>
                <p className="flex items-center gap-2 text-[0.8125rem] text-ink-2">
                  <Mail className="size-3.5 shrink-0" aria-hidden="true" />
                  You can also just reply to our email — it lands here.
                </p>
              </div>
            </form>
          </StripBody>
        </Strip>
      </section>
    </>
  );
}

/**
 * One message.
 *
 * Staff messages carry the leading bar and the customer's do not, so the eye
 * finds the answers when scrolling a long thread — the customer already knows
 * what they wrote.
 */
function Message({ message, ticketId }: { message: TicketMessage; ticketId: string }) {
  const fromStaff = message.author_role === "staff";

  return (
    <Strip
      tone={fromStaff ? "active" : "neutral"}
      bar={fromStaff}
      className={cn(fromStaff && "pl-1")}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-rule px-4 py-2.5">
        <span className="t-field text-ink">
          {fromStaff ? (message.author_name ?? "Downwind Studio") : "You"}
        </span>
        <span className="t-field" title={stamp(message.created_at)}>
          {relative(message.created_at)}
          {/* Worth surfacing: a reply that came in by email has been through
              quoted-history stripping, so a terse-looking message may have had
              more around it. */}
          {message.via === "email" ? " · by email" : ""}
        </span>
      </div>

      <StripBody>
        {/* A measure on the body, not on the strip. The panel lines up with
            every other one in the portal while the sentences stay at a length
            an eye can track back from — the attachments below deliberately
            keep the full width, because thumbnails want to wrap. */}
        {message.body ? (
          <p className="max-w-[68ch] whitespace-pre-wrap text-[0.9375rem] leading-relaxed text-ink">
            {message.body}
          </p>
        ) : (
          <p className="max-w-[68ch] text-[0.9375rem] italic leading-relaxed text-ink-2">
            No text — see the image{message.attachments.length === 1 ? "" : "s"} below.
          </p>
        )}

        {message.attachments.length > 0 ? (
          <ul className="mt-4 flex flex-wrap gap-3">
            {message.attachments.map((a) => (
              <li key={a.id}>
                <Attachment attachment={a} ticketId={ticketId} />
              </li>
            ))}
          </ul>
        ) : null}
      </StripBody>
    </Strip>
  );
}

/**
 * A thumbnail linking to the full image.
 *
 * Both the preview and the link go to the same authenticated Worker route —
 * there is no public bucket URL to hand out, and the session cookie the browser
 * already holds is what authorises the read.
 */
function Attachment({
  attachment,
  ticketId,
}: {
  attachment: AttachmentMeta;
  ticketId: string;
}) {
  const url = portalApi.attachmentUrl(ticketId, attachment.id);
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="block w-28 overflow-hidden rounded-[2px] border border-rule bg-sunk transition-colors hover:border-ink-3"
      title={attachment.filename}
    >
      <img
        src={url}
        alt={attachment.filename}
        loading="lazy"
        className="block h-20 w-full object-cover"
      />
      <span className="block truncate px-1.5 py-1 text-[0.6875rem] text-ink-2">
        {attachment.filename}
      </span>
    </a>
  );
}

function BackButton() {
  return (
    <Button asChild variant="ghost" size="sm">
      <Link to="/support">
        <ArrowLeft aria-hidden="true" />
        All tickets
      </Link>
    </Button>
  );
}
